import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo, useRef } from 'react';
import { ethers, BrowserProvider, JsonRpcSigner, Signer, WebSocketProvider } from 'ethers';
import { formatCompactBNB } from '@/lib/ethersUtils';
import { isPageVisible } from '@/lib/pageVisibility';
import {
  RaffleMiningContract,
  RaffleMiningViewsContract,
  WelephantRewardsContract,
  AutoPlayDepositContract,
  PayDayInfoListContract,
  createRaffleMiningContract,
  createViewsContract,
  createWelephantRewardsContract,
  createAutoPlayDepositContract,
  createPayDayInfoListContract,
  CONTRACT_ADDRESS,
  VIEWS_CONTRACT_ADDRESS,
  SWAPPER_ADDRESS,
  WELEPHANT_REWARDS_ADDRESS,
  AUTO_PLAY_DEPOSIT_ADDRESS,
  PAYDAY_INFO_LIST_ADDRESS,
  GAME_CHAIN_ID,
} from '@/lib/contracts';
import { getChainProvider } from '@/lib/chainProvider';
import { EIP1193Provider } from '@/hooks/useWalletDiscovery';
import { getWebSocketUrl, isWebSocketAvailable } from '@/lib/chains';

// Re-export contract addresses for convenience
export { CONTRACT_ADDRESS, VIEWS_CONTRACT_ADDRESS, SWAPPER_ADDRESS, WELEPHANT_REWARDS_ADDRESS, AUTO_PLAY_DEPOSIT_ADDRESS, PAYDAY_INFO_LIST_ADDRESS };

// Extend Window interface for ethereum provider
declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

// Configuration
// DEV_MODE is used for UI indicators and logging, but wallet connection uses MetaMask in both modes
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';
const DEV_CHAIN_ID = 1337;
const PROD_CHAIN_ID = 56; // BNB Chain

// Hardhat local network configuration
const HARDHAT_NETWORK_PARAMS = {
  chainId: `0x${DEV_CHAIN_ID.toString(16)}`, // 0x539
  chainName: 'Hardhat Local',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: ['http://127.0.0.1:8545'],
  blockExplorerUrls: [],
};

// BNB Chain (BSC Mainnet) configuration
const BNB_NETWORK_PARAMS = {
  chainId: `0x${PROD_CHAIN_ID.toString(16)}`, // 0x38
  chainName: 'BNB Smart Chain',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: ['https://bsc-dataseed.binance.org/'],
  blockExplorerUrls: ['https://bscscan.com/'],
};

// Get the expected chain ID and network params based on mode
const EXPECTED_CHAIN_ID = DEV_MODE ? DEV_CHAIN_ID : PROD_CHAIN_ID;
const NETWORK_PARAMS = DEV_MODE ? HARDHAT_NETWORK_PARAMS : BNB_NETWORK_PARAMS;

interface EthersContextValue {
  // Provider state
  provider: BrowserProvider | null;
  signer: JsonRpcSigner | null;

  // WebSocket provider for real-time event subscriptions
  wsProvider: WebSocketProvider | null;
  wsConnected: boolean;
  wsReconnect: () => void;

  // Contract instances - first-class citizens for direct function calls
  raffleMining: RaffleMiningContract | null;
  viewsContract: RaffleMiningViewsContract | null;
  welephantRewards: WelephantRewardsContract | null;
  autoPlayDeposit: AutoPlayDepositContract | null;
  payDayInfoList: PayDayInfoListContract | null;

  // Account state
  address: string | undefined;
  isConnected: boolean;
  providerReady: boolean;
  balance: bigint | undefined;
  formattedBalance: string;

  // RPC connectivity
  rpcConnected: boolean;
  lastBlockNumber: number | undefined;

  // Connection actions
  connect: (selectedProvider?: EIP1193Provider) => Promise<void>;
  disconnect: () => void;
  checkRpcConnection: () => Promise<boolean>;

  // Utility
  isDevMode: boolean;
  chainId: number | undefined;
}

const EthersContext = createContext<EthersContextValue | null>(null);

interface EthersProviderProps {
  children: ReactNode;
}

// WebSocket reconnection constants
const WS_MAX_RECONNECT_ATTEMPTS = 5;
const WS_RECONNECT_DELAY_MS = 2000;

export function EthersProvider({ children }: EthersProviderProps) {
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [signer, setSigner] = useState<JsonRpcSigner | null>(null);
  const [address, setAddress] = useState<string | undefined>(undefined);
  const [balance, setBalance] = useState<bigint | undefined>(undefined);
  const [chainId, setChainId] = useState<number | undefined>(undefined);

  // RPC connectivity state
  const [rpcConnected, setRpcConnected] = useState<boolean>(false);
  const [lastBlockNumber, setLastBlockNumber] = useState<number | undefined>(undefined);

  // WebSocket provider state
  const [wsProvider, setWsProvider] = useState<WebSocketProvider | null>(null);
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const wsReconnectAttemptsRef = useRef<number>(0);
  const wsReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Provider initialization complete (listAccounts resolved)
  const [providerReady, setProviderReady] = useState(false);

  // Contract instances - created when provider is available
  const [raffleMining, setRaffleMining] = useState<RaffleMiningContract | null>(null);
  const [viewsContract, setViewsContract] = useState<RaffleMiningViewsContract | null>(null);
  const [welephantRewards, setWelephantRewards] = useState<WelephantRewardsContract | null>(null);
  const [autoPlayDeposit, setAutoPlayDeposit] = useState<AutoPlayDepositContract | null>(null);
  const [payDayInfoList, setPayDayInfoList] = useState<PayDayInfoListContract | null>(null);

  // Check RPC connectivity by fetching block number
  const checkRpcConnection = useCallback(async (): Promise<boolean> => {
    if (!provider) {
      setRpcConnected(false);
      return false;
    }

    try {
      const blockNumber = await provider.getBlockNumber();
      setLastBlockNumber(blockNumber);
      setRpcConnected(true);
      return true;
    } catch (error) {
      setRpcConnected(false);
      console.error('RPC connectivity check failed:', error instanceof Error ? error.message : String(error));
      return false;
    }
  }, [provider]);

  // WebSocket connection setup
  const connectWebSocket = useCallback(async () => {
    const wsUrl = getWebSocketUrl(EXPECTED_CHAIN_ID);
    if (!wsUrl) {
      console.log('[EthersProvider] WebSocket not available for chain', EXPECTED_CHAIN_ID);
      return;
    }

    try {
      console.log('[EthersProvider] Connecting WebSocket to', wsUrl.replace(/\/[^/]+$/, '/***'));
      const ws = new WebSocketProvider(wsUrl);

      // Wait for provider to be ready
      await ws.ready;

      // Verify connection
      const blockNumber = await ws.getBlockNumber();
      console.log('[EthersProvider] WebSocket connected, block:', blockNumber);

      // Validate subscription support (needed for contract.on() events)
      try {
        const subId = await ws.send('eth_subscribe', ['newHeads']);
        await ws.send('eth_unsubscribe', [subId]);
        console.log('[EthersProvider] WebSocket subscription support verified');
      } catch (subError) {
        console.warn('[EthersProvider] WebSocket endpoint does not support subscriptions, using polling:', subError);
        ws.destroy();
        return;
      }

      // Set up close handler for reconnection
      ws.websocket.addEventListener('close', () => {
        console.log('[EthersProvider] WebSocket closed');
        setWsConnected(false);
        setWsProvider(null);

        // Attempt reconnection with backoff
        if (wsReconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          wsReconnectAttemptsRef.current++;
          const delay = WS_RECONNECT_DELAY_MS * wsReconnectAttemptsRef.current;
          console.log(`[EthersProvider] WebSocket reconnecting in ${delay}ms (attempt ${wsReconnectAttemptsRef.current}/${WS_MAX_RECONNECT_ATTEMPTS})`);

          wsReconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else {
          console.error('[EthersProvider] WebSocket max reconnection attempts reached');
        }
      });

      ws.websocket.addEventListener('error', (event) => {
        console.error('[EthersProvider] WebSocket error:', event);
      });

      setWsProvider(ws);
      setWsConnected(true);
      wsReconnectAttemptsRef.current = 0;
    } catch (error) {
      console.error('[EthersProvider] WebSocket connection failed:', error);
      setWsConnected(false);
    }
  }, []);

  // Manual reconnect function
  const wsReconnect = useCallback(() => {
    // Clear any pending reconnect timeout
    if (wsReconnectTimeoutRef.current) {
      clearTimeout(wsReconnectTimeoutRef.current);
      wsReconnectTimeoutRef.current = null;
    }

    // Disconnect existing connection
    if (wsProvider) {
      try {
        wsProvider.destroy();
      } catch {
        // Ignore cleanup errors
      }
      setWsProvider(null);
      setWsConnected(false);
    }

    // Reset attempts and reconnect
    wsReconnectAttemptsRef.current = 0;
    connectWebSocket();
  }, [wsProvider, connectWebSocket]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!isWebSocketAvailable(EXPECTED_CHAIN_ID)) {
      return;
    }

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
      }
      if (wsProvider) {
        try {
          wsProvider.destroy();
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, [connectWebSocket]);

  // Initialize provider and contracts
  useEffect(() => {
    const initProvider = async () => {
      try {
        if (typeof window !== 'undefined' && window.ethereum) {
          // Use BrowserProvider (MetaMask/injected wallet) for both dev and production
          const browserProvider = new BrowserProvider(window.ethereum);
          setProvider(browserProvider);

          // Create contract instances with provider (read-only)
          const raffleMiningInstance = createRaffleMiningContract(browserProvider);
          const viewsInstance = createViewsContract(browserProvider);
          const welephantRewardsInstance = createWelephantRewardsContract(browserProvider);
          const autoPlayDepositInstance = createAutoPlayDepositContract(browserProvider);
          const payDayInfoListInstance = PAYDAY_INFO_LIST_ADDRESS ? createPayDayInfoListContract(browserProvider) : null;
          setRaffleMining(raffleMiningInstance);
          setViewsContract(viewsInstance);
          setWelephantRewards(welephantRewardsInstance);
          setAutoPlayDeposit(autoPlayDepositInstance);
          setPayDayInfoList(payDayInfoListInstance);

          // Verify RPC connectivity via browser provider
          try {
            const blockNumber = await browserProvider.getBlockNumber();
            setLastBlockNumber(blockNumber);
            setRpcConnected(true);
          } catch (rpcError) {
            setRpcConnected(false);
            console.error('RPC connection failed:', rpcError instanceof Error ? rpcError.message : String(rpcError));
          }

          // Check if already connected
          const accounts = await browserProvider.listAccounts();
          if (accounts.length > 0) {
            const signer = await browserProvider.getSigner();
            setSigner(signer);
            const addr = await signer.getAddress();
            setAddress(addr);

            const network = await browserProvider.getNetwork();
            setChainId(Number(network.chainId));
          }
        } else {
          // No wallet detected — use read-only JsonRpcProvider so pages can still display data
          console.log('[EthersProvider] No wallet detected, using read-only provider');
          try {
            const readOnlyProvider = await getChainProvider(GAME_CHAIN_ID);

            const raffleMiningInstance = createRaffleMiningContract(readOnlyProvider);
            const viewsInstance = createViewsContract(readOnlyProvider);
            const welephantRewardsInstance = createWelephantRewardsContract(readOnlyProvider);
            const autoPlayDepositInstance = createAutoPlayDepositContract(readOnlyProvider);
            const payDayInfoListInstance = PAYDAY_INFO_LIST_ADDRESS ? createPayDayInfoListContract(readOnlyProvider) : null;
            setRaffleMining(raffleMiningInstance);
            setViewsContract(viewsInstance);
            setWelephantRewards(welephantRewardsInstance);
            setAutoPlayDeposit(autoPlayDepositInstance);
            setPayDayInfoList(payDayInfoListInstance);

            const blockNumber = await readOnlyProvider.getBlockNumber();
            setLastBlockNumber(blockNumber);
            setRpcConnected(true);
          } catch (rpcError) {
            setRpcConnected(false);
            console.error('Read-only RPC connection failed:', rpcError instanceof Error ? rpcError.message : String(rpcError));
          }
        }
      } catch (error) {
        console.error('Provider initialization failed:', error);
      } finally {
        setProviderReady(true);
      }
    };

    initProvider();
  }, []);

  // Fetch balance periodically
  useEffect(() => {
    if (!provider || !address) return;

    const fetchBalance = async () => {
      if (!isPageVisible()) return;
      try {
        const bal = await provider.getBalance(address);
        setBalance(bal);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorCode = (error as { code?: string })?.code;
        console.error('[EthersProvider] Balance fetch error:', { message: errorMessage, code: errorCode });
      }
    };

    fetchBalance();
    const interval = setInterval(fetchBalance, 5000);
    return () => clearInterval(interval);
  }, [provider, address]);

  // Listen for account changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.ethereum) return;

    const ethereum = window.ethereum;

    const handleAccountsChanged = async (...args: unknown[]) => {
      const accounts = args[0] as string[];
      if (!accounts || accounts.length === 0) {
        setSigner(null);
        setAddress(undefined);
        setBalance(undefined);
      } else if (provider && provider instanceof BrowserProvider) {
        const newSigner = await provider.getSigner();
        setSigner(newSigner);
        setAddress(accounts[0]);
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    ethereum.on('accountsChanged', handleAccountsChanged);
    ethereum.on('chainChanged', handleChainChanged);

    return () => {
      ethereum.removeListener('accountsChanged', handleAccountsChanged);
      ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [provider]);

  const connect = useCallback(async (selectedProvider?: EIP1193Provider) => {
    // Use selected provider or fall back to window.ethereum
    const walletProvider = selectedProvider || window.ethereum;

    if (typeof window === 'undefined' || !walletProvider) {
      throw new Error('No wallet detected. Please install a Web3 wallet.');
    }

    try {
      let browserProvider = new BrowserProvider(walletProvider);
      await browserProvider.send('eth_requestAccounts', []);

      // Ensure we're on the correct chain (1337 for dev, 56 for prod)
      const network = await browserProvider.getNetwork();
      const currentChainId = Number(network.chainId);

      if (currentChainId !== EXPECTED_CHAIN_ID) {
        try {
          // Try to switch to the expected network
          await walletProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: NETWORK_PARAMS.chainId }],
          });
        } catch (switchError: unknown) {
          // Chain doesn't exist in wallet, add it
          const error = switchError as { code?: number };
          if (error.code === 4902) {
            await walletProvider.request({
              method: 'wallet_addEthereumChain',
              params: [NETWORK_PARAMS],
            });
          } else {
            throw switchError;
          }
        }

        // Recreate provider after chain switch
        browserProvider = new BrowserProvider(walletProvider);
      }

      const signer = await browserProvider.getSigner();
      const address = await signer.getAddress();
      const finalNetwork = await browserProvider.getNetwork();

      setProvider(browserProvider);
      setSigner(signer);
      setAddress(address);
      setChainId(Number(finalNetwork.chainId));

      // Update contract instances with new provider
      const raffleMiningInstance = createRaffleMiningContract(browserProvider);
      const viewsInstance = createViewsContract(browserProvider);
      const welephantRewardsInstance = createWelephantRewardsContract(browserProvider);
      const autoPlayDepositInstance = createAutoPlayDepositContract(browserProvider);
      const payDayInfoListInstance = PAYDAY_INFO_LIST_ADDRESS ? createPayDayInfoListContract(browserProvider) : null;
      setRaffleMining(raffleMiningInstance);
      setViewsContract(viewsInstance);
      setWelephantRewards(welephantRewardsInstance);
      setAutoPlayDeposit(autoPlayDepositInstance);
      setPayDayInfoList(payDayInfoListInstance);

      // Verify RPC connectivity
      try {
        const blockNumber = await browserProvider.getBlockNumber();
        setLastBlockNumber(blockNumber);
        setRpcConnected(true);
      } catch (rpcError) {
        setRpcConnected(false);
        console.error('RPC connection failed after wallet connect:', rpcError instanceof Error ? rpcError.message : String(rpcError));
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    }
  }, []);

  const disconnect = useCallback(() => {
    setSigner(null);
    setAddress(undefined);
    setBalance(undefined);
    setChainId(undefined);
  }, []);

  const value = useMemo<EthersContextValue>(() => ({
    provider,
    signer,
    wsProvider,
    wsConnected,
    wsReconnect,
    raffleMining,
    viewsContract,
    welephantRewards,
    autoPlayDeposit,
    payDayInfoList,
    address,
    isConnected: !!address && !!signer,
    providerReady,
    balance,
    formattedBalance: balance !== undefined ? formatCompactBNB(balance) : '0',
    rpcConnected,
    lastBlockNumber,
    connect,
    disconnect,
    checkRpcConnection,
    isDevMode: DEV_MODE,
    chainId,
  }), [provider, signer, wsProvider, wsConnected, wsReconnect, raffleMining, viewsContract, welephantRewards, autoPlayDeposit, payDayInfoList, address, providerReady, balance, rpcConnected, lastBlockNumber, connect, disconnect, checkRpcConnection, chainId]);

  return (
    <EthersContext.Provider value={value}>
      {children}
    </EthersContext.Provider>
  );
}

export function useEthersContext(): EthersContextValue {
  const context = useContext(EthersContext);
  if (!context) {
    throw new Error('useEthersContext must be used within EthersProvider');
  }
  return context;
}

/**
 * Hook to get a signer-connected contract for write operations
 *
 * Usage:
 * const connectedContract = useSignerContract();
 * if (connectedContract) {
 *   await connectedContract.play(players, squares, amounts, { value });
 * }
 */
export function useSignerContract(): RaffleMiningContract | null {
  const { raffleMining, signer } = useEthersContext();

  return useMemo(() => {
    if (!raffleMining || !signer) return null;
    return raffleMining.connect(signer as Signer) as RaffleMiningContract;
  }, [raffleMining, signer]);
}

// Re-export for convenience
export { DEV_MODE as isDevMode };
